import React, { useContext } from 'react'
import styled, { useTheme } from 'particles/themes/styled'
import { ButtonProps } from 'rebass'
import { theme } from 'theme'
import InfoTooltip from 'components/InfoTooltip'
import { useTranslation } from 'react-i18next'
import { getFilterOrSortTooltipMenuItem } from './FilterAndSort/FilterOrSortTooltip'
import { OptionsState } from 'particles/Dropdown'
import RectangleButton from 'particles/Button/RectangleButton'
import Tooltip from 'particles/Tooltip'
import IconButton, { IconButtonProps } from 'particles/Button/IconButton'
import Menu from 'particles/Menu'
import { TableHeaderContext } from './PartiTable'
import { TableViewType } from '.'
import { useIsMobileView } from 'hooks'

export interface ButtonWithInfoProps extends ButtonProps {
    key?: string
    iconSize?: any
    color?: string
    disabled?: boolean
    isActive?: boolean
    textProps?: any
    hoverBg?: string
    text?: string
    textColor?: string
    iconColor?: string
}
interface BaseButtonProps {
    isDisable?: boolean
    isActive?: boolean
    infoTitle?: string
    infoLabel?: React.ReactNode
}
export interface ActionButtonProps extends BaseButtonProps {
    onClick: (e?: React.MouseEvent<HTMLElement>) => void
}

interface FilterOrSortButtonProps extends BaseButtonProps {
    isSort?: boolean
    anchorEl: HTMLElement | null
    onClick: (e?: React.MouseEvent<HTMLElement>) => void
    showList?: boolean
    onAddFilterField: (option: OptionsState) => void
    viewType?: TableViewType
}
interface MoveToListButtonProps extends ActionButtonProps {
    text: string
    isWaitList?: boolean
    isPriorityList?: boolean
    iconSize?: string
}
interface ImportButtonProps extends ActionButtonProps {
    text: string
}

export const iconStyle = {
    yellowIcon: {
        width: 16,
        height: 16,
        stroke: theme.newColors.neutral50,
        fill: theme.colors.orange,
        display: 'block',
    },
}

type TableIconButtonProps = Omit<IconButtonProps, 'icon'>

interface BaseIconButtonProps extends TableIconButtonProps {
    bgColor: string
}
const BaseIconButton = styled(IconButton)<BaseIconButtonProps>`
    background-color: ${({ theme, bgColor, isDisabled }) => (isDisabled ? theme.colors.surface.disable : bgColor)};
    &:focus {
        outline: none;
        cursor: pointer;
    }
    &:hover:not([disabled]) {
        cursor: pointer;
        outline: none;
    }
`

export const IconButtonCheckSquare: React.FC<TableIconButtonProps> = ({ size = 'xs', isDisabled = false, onClick }) => {
    const [t] = useTranslation()
    const theme = useTheme()
    return (
        <Tooltip label={t('button.approve')}>
            <BaseIconButton
                icon="checkSquare"
                size={size}
                bgColor={theme.colors.surface.positive}
                isDisabled={isDisabled}
                onClick={onClick}
                color="neutral"
            />
        </Tooltip>
    )
}

export const IconButtonEdit: React.FC<TableIconButtonProps> = ({ size = 'xs', isDisabled = false, onClick }) => {
    const [t] = useTranslation()
    const theme = useTheme()
    return (
        <Tooltip label={t('button.editsRequested')}>
            <BaseIconButton
                size={size}
                icon="pencilSimple"
                bgColor={theme.colors.surface.warning}
                isDisabled={isDisabled}
                onClick={onClick}
                color="neutral"
            />
        </Tooltip>
    )
}

export const IconButtonXSquare: React.FC<TableIconButtonProps> = ({ size = 'xs', isDisabled = false, onClick }) => {
    const [t] = useTranslation()
    const theme = useTheme()
    return (
        <Tooltip label={t('button.reject')}>
            <BaseIconButton
                size={size}
                icon="xCircle"
                bgColor={theme.colors.surface.error}
                isDisabled={isDisabled}
                onClick={onClick}
                color="neutral"
            />
        </Tooltip>
    )
}

export const ApproveButton: React.FC<ActionButtonProps> = ({ isDisable, onClick }) => {
    const [t] = useTranslation()
    const isMobileView = useIsMobileView()
    return isMobileView ? (
        <IconButton form="icon" color="neutral" size="m" icon="checkSquare" onClick={onClick} isDisabled={isDisable} />
    ) : (
        <RectangleButton
            form="text"
            size="s"
            color="neutral"
            label={t('button.approve')}
            icon="checkSquare"
            onClick={onClick}
            isDisabled={isDisable}
        />
    )
}
const MoveToListButtonContainer = styled.div`
    display: flex;
    align-items: center;
    min-width: '159px';
    box-sizing: border-box;
    .info-tooltip {
        margin: 0;
    }
`

export const MoveToListButton: React.FC<MoveToListButtonProps> = ({
    infoTitle,
    infoLabel,
    text,
    onClick,
    isDisable,
    isWaitList = false,
    isPriorityList = false,
}) => {
    const isMobileView = useIsMobileView()
    return isMobileView ? (
        <IconButton
            form="icon"
            size="m"
            color="neutral"
            icon={isWaitList ? 'clockCountdown' : isPriorityList ? 'listNumbers' : 'arrowUDownLeft'}
            isDisabled={isDisable}
            onClick={onClick}
        />
    ) : (
        <MoveToListButtonContainer>
            <RectangleButton
                form="text"
                size="s"
                color="neutral"
                isDisabled={isDisable}
                onClick={onClick}
                label={text}
                icon={isWaitList ? 'clockCountdown' : isPriorityList ? 'listNumbers' : 'arrowUDownLeft'}
                className="width-fit-content"
            />
            {infoLabel || infoTitle ? (
                <InfoTooltip
                    title={infoTitle}
                    label={infoLabel}
                    size="16px"
                    iconColor={isDisable ? 'disable' : 'neutralAction'}
                />
            ) : null}
        </MoveToListButtonContainer>
    )
}

export const ApprovalOrderButton: React.FC<ActionButtonProps> = ({ onClick }) => {
    const [t] = useTranslation()
    const isMobileView = useIsMobileView()
    return isMobileView ? (
        <IconButton form="icon" size="m" color="neutral" icon="sortDescending" onClick={onClick} />
    ) : (
        <RectangleButton
            form="text"
            size="s"
            color="neutral"
            label={t('button.approvalOrder')}
            onClick={onClick}
            icon="sortDescending"
        />
    )
}

export const ShowColumnButton: React.FC<ActionButtonProps> = ({ onClick }) => {
    const [t] = useTranslation()
    return (
        <RectangleButton
            form="text"
            size="s"
            color="neutral"
            icon="eye"
            label={t('button.showColumns')}
            onClick={onClick}
        />
    )
}

export const ExportButton: React.FC<ActionButtonProps> = ({ onClick, isDisable }) => {
    const [t] = useTranslation()
    const isMobileView = useIsMobileView()

    return isMobileView ? (
        <IconButton form="icon" size="m" color="neutral" icon="export" isDisabled={isDisable} onClick={onClick} />
    ) : (
        <RectangleButton
            form="text"
            size="s"
            color="neutral"
            icon="export"
            label={t('button.export')}
            onClick={onClick}
            isDisabled={isDisable}
        />
    )
}

export const ImportListButton: React.FC<ImportButtonProps> = ({ onClick, isDisable, text }) => {
    return (
        <RectangleButton
            form="text"
            size="s"
            color="neutral"
            icon="downloadSimple"
            label={text}
            onClick={onClick}
            isDisabled={isDisable}
        />
    )
}

export const FilterOrSortButton: React.FC<FilterOrSortButtonProps> = ({
    isSort = false,
    onClick,
    isDisable,
    showList = false,
    onAddFilterField,
    anchorEl,
    viewType,
}) => {
    const [t] = useTranslation()
    const { columns } = useContext(TableHeaderContext)
    const isMobileView = useIsMobileView()

    const items = getFilterOrSortTooltipMenuItem({
        title: isSort ? t('dropdownMenu.sortByHeader') : t('dropdownMenu.filterByHeader'),
        isSort,
        onChange: onAddFilterField,
        onClose: onClick,
        viewType,
    })

    if (!columns || !Array.isArray(columns)) return null

    return items.length > 0 ? (
        <>
            {isMobileView ? (
                <IconButton
                    form="icon"
                    size="m"
                    color="neutral"
                    icon={isSort ? 'arrowsDownUp' : 'funnel'}
                    isDisabled={isDisable}
                    onClick={onClick}
                />
            ) : (
                <RectangleButton
                    form="text"
                    size="s"
                    color="neutral"
                    icon={isSort ? 'arrowsDownUp' : 'funnel'}
                    label={isSort ? t('button.sort') : t('button.filter')}
                    onClick={onClick}
                    isDisabled={isDisable}
                />
            )}
            {showList && (
                <Menu size="s" open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => onClick()} width={216}>
                    {items}
                </Menu>
            )}
        </>
    ) : (
        <></>
    )
}
